import {
  Component,
  ContentChild,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { MultimediaListComponent } from '../../organisms/multimedia-list/multimedia-list.component';
import { MultimediaList } from '../../../shared/interfaces/multimedia-list.interface';
import { TooltipComponent } from '../tooltip/tooltip.component';
import { IconComponent } from '../../atoms/icon/icon.component';

import { Colors, Sizes } from '../../../shared/enums/shared.enums';
import { Tooltip } from '../../../shared/interfaces/tooltip.interface';
import ViewportType from '../../../shared/enums/viewport-type.enums';
import { CrossReferencing } from '../../../shared/interfaces/cross-referencing.interface';
import {
  FaIconLibrary,
  IconName,
  IconPrefix,
} from '@fortawesome/angular-fontawesome';
import {
  CaasComponent,
  CaasElement,
  CaasElementItem,
} from '../../../shared/interfaces/caas/caas.interface';
import { ImageComponent } from '../../atoms/image/image.component';
import { ActivatedRoute } from '@angular/router';
import { ImagesService } from '../../../services/images.service';
import { CaasBodytextService } from '../../../services/caas-bodytext.service';

@Component({
  selector: 'hdi-material-download',
  standalone: true,
  imports: [
    CommonModule,
    MultimediaListComponent,
    IconComponent,
    TooltipComponent,
    ImageComponent,
  ],
  templateUrl: './material-download.component.html',
  styleUrls: ['./material-download.component.scss'],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class MaterialDownloadComponent implements OnChanges {
  @ContentChild('slot') projectedContent?: ElementRef;
  @Input() caasData?: CaasComponent;
  @Input() viewport: ViewportType = ViewportType.desktop;
  @Input() headline: string = '';
  @Input() multimediaLists: MultimediaList[];
  @Input() multimediaImage: string = '';
  @Input() multimediaImageAlt: string = '';
  @Input() multimediaTitle: string = '';
  @Input() headlineItem: {
    label: string;
    leftIcon: string;
    rightIcons: { type: string; href: string; tooltip?: Tooltip }[];
  };
  @Input() crossReferencing?: CrossReferencing;
  @Input() caasCrossReferencing?: CaasComponent;
  baseHref: string = '';
  clipboardTooltip?: Tooltip;

  Sizes = Sizes;
  Colors = Colors;

  tooltip?: Tooltip | null;

  constructor(
    private library: FaIconLibrary,
    private caasBodytextService: CaasBodytextService,
    @Inject(DOCUMENT) private document: Document,
    private activatedRoute: ActivatedRoute,
    private imagesService: ImagesService
  ) {
    this.multimediaLists = [];
    this.headlineItem = { label: '', leftIcon: 'pdf', rightIcons: [] };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['caasData']) {
      const baseElement = this.document.querySelector('base');
      const angularRoute = this.activatedRoute.routeConfig?.path;
      if (angularRoute && baseElement?.href) {
        this.baseHref = baseElement.href + '' + angularRoute;
      } else {
        this.baseHref = baseElement?.href ?? '';
      }

      this.setCaasContent();
    }
  }

  closeTooltip() {
    this.tooltip = null;
  }
  openTooltip(tooltip: Tooltip) {
    this.tooltip = tooltip;
  }

  getIconName(name: string): string {
    if (
      this.library.getIconDefinition('far' as IconPrefix, name as IconName) !==
      null
    ) {
      return name;
    } else {
      return 'user';
    }
  }

  copyToClipboard(url: string, event: TouchEvent | MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    navigator.clipboard.writeText(url);
    this.clipboardTooltip = {
      id: this.tooltip?.id ?? '',
      description: '<span>URL kopiert</span>',
    };
    setTimeout(() => {
      this.clipboardTooltip = undefined;
    }, 500);
  }

  setCaasContent() {
    this.headline = this.caasData!.formData.st_header!.value;
    this.multimediaLists = this.getCaasMultimediaLists(this.caasData!);
    if (
      this.caasData?.formData.st_elements?.value[0].template.uid ===
      'st_download_container'
    ) {
      this.getCaasMultimediaHeadline(
        this.caasData?.formData.st_elements?.value[0]
      );
    }
  }

  getCaasMultimediaHeadline(component: CaasElement) {
    this.multimediaTitle = component.formData.st_featured_title
      ?.value as string;
    this.multimediaImage = component.formData.st_featured_image?.value
      .url as string;
    if (component.formData.st_featured_image?.value?.identifier) {
      this.multimediaImageAlt =
        this.imagesService.getAltTextById(
          component.formData.st_featured_image.value.identifier
        ) ?? '';
    }
    this.headlineItem = {
      label: component.formData.st_featured_link?.value.formData?.lt_link_label
        ?.value as string,
      leftIcon: 'file-pdf',
      rightIcons:
        !component.formData.st_featured_link?.value.formData?.lt_link_external?.value?.includes(
          'https://www.hdi-brandportal.com'
        )
          ? [
              {
                type: 'eye',
                href: component.formData.st_featured_link?.value.formData
                  ?.lt_link_external?.value as string,
                tooltip: {
                  id: crypto.randomUUID().toString(),
                  description: '<span>Ansicht</span>',
                },
              },
              {
                type: 'down-to-line',
                href: component.formData.st_featured_link?.value.formData
                  ?.lt_link_external?.value as string,
                tooltip: {
                  id: crypto.randomUUID().toString(),
                  description: '<span>Download</span>',
                },
              },
              {
                type: 'link',
                href: component.formData.st_featured_link?.value.formData
                  ?.lt_link_external?.value as string,
                tooltip: {
                  id: crypto.randomUUID().toString(),
                  description: '<span>Link kopieren</span>',
                },
              },
            ]
          : [
              {
                type: 'eye',
                href: component.formData.st_featured_link?.value.formData
                  ?.lt_link_external?.value as string,
                tooltip: {
                  id: crypto.randomUUID().toString(),
                  description: '<span>Ansicht</span>',
                },
              },
              {
                type: 'link',
                href: component.formData.st_featured_link?.value.formData
                  ?.lt_link_external?.value as string,
                tooltip: {
                  id: crypto.randomUUID().toString(),
                  description: '<span>Link kopieren</span>',
                },
              },
            ],
    };
  }

  getCaasMultimediaLists(component: CaasComponent): MultimediaList[] {
    const downloads =
      component?.formData?.st_elements?.value?.find(
        (multimediaItem) =>
          multimediaItem?.template?.uid === 'st_download_container'
      ) ?? null;

    const multimedia =
      component?.formData?.st_elements?.value?.find(
        (multimediaItem) =>
          multimediaItem?.template?.uid === 'st_multimedia_container'
      ) ?? null;

    if (!downloads && !multimedia) {
      console.warn('debug > No valid st_elements found in caasData');
    }
    const downloadList = {
      title: downloads?.formData?.st_header?.value ?? '',
      key: downloads?.identifier ?? '',
      backgroundColor: Colors.NEUTRAL_LIGHTER,
      items: downloads?.formData?.st_elements?.value
        ?.map((downloadItem) => {
          if (!downloadItem?.formData) return null;
          return {
            label: downloadItem?.formData?.lt_link_label?.value ?? '',
            link: '',
            leftIcon: 'file-pdf' as string | undefined,
            rightIcons:
              !downloadItem?.formData?.lt_link_external?.value?.includes(
                'https://www.hdi-brandportal.com'
              )
                ? [
                    {
                      type: 'eye',
                      href:
                        downloadItem?.formData?.lt_link_external?.value ?? '',
                      target: '_blank',
                      tooltip: {
                        id: crypto.randomUUID().toString(),
                        description: '<span>Ansicht</span>',
                      },
                      title: 'Ansicht',
                    },
                    {
                      type: 'down-to-line',
                      href:
                        downloadItem?.formData?.lt_link_external?.value ?? '',
                      tooltip: {
                        id: crypto.randomUUID().toString(),
                        description: '<span>Download</span>',
                      },
                      title: 'Download',
                    },
                    {
                      type: 'link',
                      href:
                        downloadItem?.formData?.lt_link_external?.value ?? '',
                      tooltip: {
                        id: crypto.randomUUID().toString(),
                        description: '<span>Link kopieren</span>',
                      },
                      title: 'Link kopieren',
                    },
                  ]
                : [
                    {
                      type: 'eye',
                      href:
                        downloadItem?.formData?.lt_link_external?.value ?? '',
                      target: '_blank',
                      tooltip: {
                        id: crypto.randomUUID().toString(),
                        description: '<span>Ansicht</span>',
                      },
                      title: 'Ansicht',
                    },
                    {
                      type: 'link',
                      href:
                        downloadItem?.formData?.lt_link_external?.value ?? '',
                      tooltip: {
                        id: crypto.randomUUID().toString(),
                        description: '<span>Link kopieren</span>',
                      },
                      title: 'Link kopieren',
                    },
                  ],
          };
        })
        .filter(Boolean),
    } as MultimediaList;
    const multimediaList = {
      title: multimedia?.formData.st_header?.value ?? '',
      key: multimedia?.identifier ?? '',
      items: multimedia?.formData?.st_elements?.value?.map((multimediaItem) => {
        return {
          label: multimediaItem?.formData?.lt_link_label?.value ?? '',
          link: '',
          leftIcon:
            multimediaItem?.formData?.lt_link_type?.value?.identifier ===
            'video'
              ? 'video'
              : 'image',
          rightIcons: [
            {
              type:
                multimediaItem?.formData?.lt_link_type?.value?.identifier ===
                'video'
                  ? 'circle-play'
                  : 'arrow-up-right-from-square',
              href: multimediaItem?.formData?.lt_link_external?.value ?? '',
              target: '_blank',
              videoSrc: this.getVideoSrc(multimediaItem),
              thumbnailSrc: '', // TODO: Add thumbnail src',
              videoId: this.getVideoId(multimediaItem),
              tooltip: {
                id: crypto.randomUUID().toString(),
                description:
                  multimediaItem?.formData?.lt_link_type?.value?.identifier ===
                  'video'
                    ? '<span>Abspielen</span>'
                    : '<span>Öffnen</span>',
              },
              title:
                multimediaItem?.formData?.lt_link_type?.value?.identifier ===
                'video'
                  ? 'Abspielen'
                  : 'Öffnen',
            },
            {
              type: 'link',
              href:
                multimediaItem?.formData?.lt_link_type?.value?.identifier !==
                'video'
                  ? multimediaItem?.formData?.lt_link_external?.value
                  : this.getClipboardVideoSrc(multimediaItem),
              tooltip: {
                id: crypto.randomUUID().toString(),
                description: '<span>Link kopieren</span>',
              },
              title: 'Link kopieren',
            },
          ],
        };
      }),
    } as MultimediaList;
    return [downloadList, multimediaList];
  }

  getVideoSrc(elementItem: CaasElementItem) {
    if (
      elementItem?.formData?.lt_link_video_type &&
      elementItem?.formData?.lt_link_vimeo_id &&
      elementItem?.formData?.lt_link_youtube_id
    ) {
      return this.caasBodytextService.getVideoSrc(
        elementItem?.formData?.lt_link_video_type,
        elementItem?.formData?.lt_link_vimeo_id,
        elementItem?.formData?.lt_link_youtube_id
      );
    }
    return '';
  }

  getVideoId(elementItem: CaasElementItem) {
    if (
      elementItem?.formData?.lt_link_video_type &&
      elementItem?.formData?.lt_link_vimeo_id &&
      elementItem?.formData?.lt_link_youtube_id
    ) {
      return this.caasBodytextService.getVideoId(
        elementItem.formData.lt_link_video_type,
        elementItem.formData.lt_link_vimeo_id,
        elementItem.formData.lt_link_youtube_id
      );
    }
    return '';
  }

  getClipboardVideoSrc(elementItem: CaasElementItem) {
    const videoId = this.getVideoId(elementItem);

    return this.baseHref + '/?videoId=' + videoId;
  }
}
